import {FC, useCallback, useEffect, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import {ActionButton} from "@fluentui/react";
import axios, {AxiosResponse} from "axios";
import {TitleAndContentLayout} from "../components/TitleAndContentLayout";
import {TenantList} from "../components/TenantList";
import {TenantResource} from "../enums/TenantResource";
import {CompanySettings, LabelContinuousOrSingleSheetEnum, LabelDefinition,} from "../API";
import {MakeTenantColumns} from "../helpers/MakeTenantColumns";
import {
    CustomSearchBoxElement,
    EnumDropdownElement,
    ITenantFormLayout,
    NumberElement,
    TextFieldElement,
} from "../components/OnixElements";
import {AlertApiError} from "../components/ShowApiError";

const LabelDefinitionsPage: FC = () => {
    const {t} = useTranslation();
    const baseDto = useMemo(() => ({}), []);
    const columns = useMemo(
        () =>
            MakeTenantColumns<LabelDefinition>(
                {
                    id: {
                        maxWidth: 20,
                    },
                    manufacturer: {},
                    manufacturerId: {},
                    name: {
                        minWidth: 300,
                    },
                    continuousOrSingleSheet: {},
                    lHDist: {},
                    lVDist: {},
                    lWidth: {},
                    lHeight: {},
                    lLeft: {},
                    lUpper: {},
                    nCols: {},
                    nRows: {},
                    lPWidth: {},
                    lPHeight: {},
                },
                t,
            ),
        [t],
    );

    const form = useMemo<ITenantFormLayout<LabelDefinition>>(
        () => ({
            manufacturer: {
                type: new TextFieldElement({}),
            },
            manufacturerId: {
                type: new TextFieldElement({}),
            },
            name: {
                type: new TextFieldElement({}),
            },
            continuousOrSingleSheet: {
                type: new EnumDropdownElement({
                    enumData: LabelContinuousOrSingleSheetEnum,
                    showCancel: true,
                }),
                enum: "ContinuousOrSingleSheet",
            },
            lHDist: {
                type: new NumberElement({}),
                translationKey: "Label-lHDistance",
            },
            lVDist: {
                type: new NumberElement({}),
                translationKey: "Label-lVDistance",
            },
            lWidth: {
                type: new NumberElement({}),
                translationKey: "Label-lWidth",
            },
            lHeight: {
                type: new NumberElement({}),
                translationKey: "Label-lHeight",
            },
            lLeft: {
                type: new NumberElement({}),
                translationKey: "Label-lLeft",
            },
            lUpper: {
                type: new NumberElement({}),
                translationKey: "Label-lUpper",
            },
            nCols: {
                type: new NumberElement({}),
                translationKey: "Label-nCols",
            },
            nRows: {
                type: new NumberElement({}),
                translationKey: "Label-nRows",
            },
            lPWidth: {
                type: new NumberElement({}),
                translationKey: "Label-lPWidth",
            },
            lPHeight: {
                type: new NumberElement({}),
                translationKey: "Label-lPHeight",
            },
            companySettingsId: {
                type: new CustomSearchBoxElement<CompanySettings, LabelDefinition>({
                    resource: TenantResource.AdminSingleCompanyTenant,
                    titleKey: "connectionName",
                    key: "id",
                    customSearchBoxType: "DEFAULT_CRUD",
                }),
            },
        }),
        [],
    );

    return (
        <TitleAndContentLayout title="label_definitions">
            <TenantList<LabelDefinition, LabelDefinition, LabelDefinition>
                resource={TenantResource.LabelDefinitions}
                baseDto={baseDto}
                columns={columns}
                allowSearching
                form={form}
                quickFilters={{
                    id: {
                        type: new NumberElement({}),
                    },
                    ...form,
                }}
                allowShowAll
                allowedGroupKeys={{
                    manufacturer: (mf, dto) => mf ?? " - ",
                }}
                additionalCommands={[<AddFromXMLButton/>]}
                stretchForm
                formChildren={(dto) =>
                    dto ? <DrawLabelDefinition labelDefinition={dto}/> : null
                }
            />
        </TitleAndContentLayout>
    );
};

export const DrawLabelDefinition: FC<{
    labelDefinition: LabelDefinition;
    itemSvgImage?: HTMLImageElement;
}> = ({labelDefinition, itemSvgImage}) => {
    const mmFactor = 33;

    const cvs = useRef<HTMLCanvasElement>(null);

    const mmDimensions = useMemo(
        () => ({
            pageWidth: (labelDefinition?.lPWidth ?? 0) / mmFactor,
            pageHeight: (labelDefinition?.lPHeight ?? 0) / mmFactor,
            leftPageMargin: (labelDefinition?.lLeft ?? 0) / mmFactor,
            upperPageMargin: (labelDefinition?.lUpper ?? 0) / mmFactor,
            horizontalLabelSpacing:
                ((labelDefinition?.lHDist ?? 0) - (labelDefinition?.lWidth ?? 0)) /
                mmFactor,
            verticalLabelSpacing:
                ((labelDefinition?.lVDist ?? 0) - (labelDefinition?.lHeight ?? 0)) /
                mmFactor,
            labelColumns: labelDefinition?.nCols ?? 0,
            labelRows: labelDefinition?.nRows ?? 0,
            labelWidth: (labelDefinition?.lWidth ?? 0) / mmFactor,
            labelHeight: (labelDefinition?.lHeight ?? 0) / mmFactor,
        }),
        [labelDefinition],
    );

    useEffect(() => {
        const ctx = cvs.current?.getContext("2d");
        if (ctx) {
            ctx.clearRect(0, 0, cvs.current?.width ?? 0, cvs.current?.height ?? 0);
            for (let i = 0; i < mmDimensions.labelColumns; i++) {
                for (let j = 0; j < mmDimensions.labelRows; j++) {
                    ctx.fillStyle = "#eeeeee";
                    ctx.fillRect(
                        mmDimensions.leftPageMargin +
                        i *
                        (mmDimensions.labelWidth + mmDimensions.horizontalLabelSpacing),
                        mmDimensions.upperPageMargin +
                        j *
                        (mmDimensions.labelHeight + mmDimensions.verticalLabelSpacing),
                        mmDimensions.labelWidth,
                        mmDimensions.labelHeight,
                    );
                    ctx.strokeStyle = "black";
                    ctx.fillStyle = "black";
                    if (!itemSvgImage) {
                        ctx.fillText(
                            `Etiketa ${i + 1} - ${j + 1}`,
                            mmDimensions.leftPageMargin +
                            i *
                            (mmDimensions.labelWidth +
                                mmDimensions.horizontalLabelSpacing) +
                            5,
                            mmDimensions.upperPageMargin +
                            j *
                            (mmDimensions.labelHeight +
                                mmDimensions.verticalLabelSpacing) +
                            15,
                        );
                    }
                    ctx.strokeRect(
                        mmDimensions.leftPageMargin +
                        i *
                        (mmDimensions.labelWidth + mmDimensions.horizontalLabelSpacing),
                        mmDimensions.upperPageMargin +
                        j *
                        (mmDimensions.labelHeight + mmDimensions.verticalLabelSpacing),
                        mmDimensions.labelWidth,
                        mmDimensions.labelHeight,
                    );

                    if (itemSvgImage) {
                        ctx.drawImage(
                            itemSvgImage,
                            mmDimensions.leftPageMargin +
                            i *
                            (mmDimensions.labelWidth +
                                mmDimensions.horizontalLabelSpacing),
                            mmDimensions.upperPageMargin +
                            j *
                            (mmDimensions.labelHeight +
                                mmDimensions.verticalLabelSpacing),
                            mmDimensions.labelWidth,
                            mmDimensions.labelHeight,
                        );
                    }
                }
            }
        }
    }, [mmDimensions, itemSvgImage]);

    return (
        <div>
            {labelDefinition && (
                <canvas
                    ref={cvs}
                    id="labelCanvas"
                    width={mmDimensions.pageWidth}
                    height={mmDimensions.pageHeight}
                    style={{border: "1px solid #000000"}}
                ></canvas>
            )}
        </div>
    );
};

const AddFromXMLButton: FC<{}> = ({}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const onFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (file) {
            const fd = new FormData();
            fd.append("files", file, file.name);
            axios
                .post("/LabelDefinitions/FromXML", fd, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then((res: AxiosResponse) => {
                    console.log(res);
                })
                .catch(AlertApiError);
        }
    }, []);

    return (
        <div>
            <ActionButton
                key={"AddFromXML"}
                onClick={() => {
                    inputRef.current?.click();
                }}
                iconProps={{iconName: "Upload"}}
            >
                <input type="file" hidden ref={inputRef} onChange={onFileChange}/>
                Dodaj iz XML-a
            </ActionButton>
        </div>
    );
};

export default LabelDefinitionsPage;
